import "app/styles/extra-options.scss";
import { useId } from "react";
import { motion } from "framer-motion";
import { OptionTemplate } from "shared/api/middleware-api-client";
import Checkbox, { CheckboxVariant } from "shared/ui/checkbox";
import Icon, { IconTypes } from "shared/ui/icon";
import Text from "shared/ui/typography/Text";
import { concatClassnames } from "shared/utils/concatClassnames";
import { useExtraHelper } from "../helpers/useExtraHelper";
import { amountedExtraOptionCodes } from "./extras";

interface Props {
  onShowExtraInfoClick: (option: OptionTemplate) => void;
  extraOptions: OptionTemplate[];
}
const ExtraOptionControls = ({ onShowExtraInfoClick, extraOptions }: Props) => {
  const idPrefix = useId();
  const {
    onExtraChange,
    isExtraChecked,
    handleAmountChange,
    selectedExtras,
    extra,
  } = useExtraHelper();

  const onInfoClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    option: OptionTemplate,
  ) => {
    e.stopPropagation();
    onShowExtraInfoClick(option);
  };

  return (
    <div
      className={concatClassnames(
        "gg-mb-2 gg-mt-3 gg-grid gg-w-full gg-border-r gg-border-t",
        "gg-rounded-sm gg-border-l gg-border-dark-blue-200",
        "gg-max-w-xl",
      )}
    >
      <ul>
        {extraOptions?.map((option: OptionTemplate) => {
          const isExtraFieldChecked = isExtraChecked(extra, option);
          const showAmountInputField =
            amountedExtraOptionCodes.includes(option.code) &&
            isExtraFieldChecked;

          // Find the current amount from the extra array
          const currentExtra = selectedExtras.find((e) => e.id === option.id);
          const amountValue = currentExtra?.amount ?? 1;

          // Calculated price for extra
          const extraPrice = (amountValue * option.price).toFixed(2);
          return (
            <li
              className={concatClassnames(
                "gg-col-span-1 gg-flex gg-cursor-pointer gg-items-center gg-text-left gg-text-xs hover:gg-bg-grey-500",
                "gg-relative gg-max-w-2xl gg-border-b gg-border-dark-blue-200 gg-px-3 gg-py-2.5",
              )}
              key={`${idPrefix}-extra-${option.code}`}
            >
              <label
                htmlFor={`${idPrefix}-extra-${option.code}`}
                className="gg-absolute gg-bottom-0 gg-left-0 gg-right-0 gg-top-0 gg-cursor-pointer"
              />
              <Checkbox
                id={`${idPrefix}-extra-${option.code}`}
                className="gg-mr-2.5 gg-shrink-0"
                checked={isExtraFieldChecked}
                onChange={() => onExtraChange(option)}
                aria-label={option.name}
                variant={CheckboxVariant.ROUND}
              />
              <div
                className={concatClassnames(
                  "gg-flex gg-w-full gg-flex-wrap gg-items-center gg-justify-between",
                )}
              >
                <Text className="gg-flex gg-w-full gg-items-center gg-justify-between gg-pr-2 sm:gg-w-2/3 lg:gg-w-full xl:gg-w-2/3">
                  <span className="gg-max-w-46 gg-block">{option.name}</span>
                  {option.description !== "" && (
                    <button
                      onClick={(e) => onInfoClick(e, option)}
                      data-testid="option-info-button"
                      aria-label={`Meer informatie over ${option.name}`}
                      className="gg-group gg-relative gg-shrink-0"
                    >
                      <Icon
                        name={IconTypes.INFO}
                        className="gg-ml-2 group-hover:gg-scale-110"
                        alt=""
                      />
                    </button>
                  )}
                </Text>

                <motion.input
                  initial={false}
                  animate={{
                    opacity: showAmountInputField ? 1 : 0,
                    scale: showAmountInputField ? 1 : 0.995,
                  }}
                  key="box"
                  type="number"
                  id={`${idPrefix}-quantity-${option.code}`}
                  name="quantity"
                  disabled={!showAmountInputField}
                  aria-label={`Aantal ${option.name}`}
                  min="1"
                  max="100"
                  value={amountValue}
                  onChange={(e) =>
                    handleAmountChange(option, Number(e.target.value))
                  }
                  className="gg-z-10 gg-h-[24px] gg-w-12 gg-border gg-border-dark-blue-200 gg-bg-gray-100 gg-pl-1.5 gg-text-xs gg-font-bold gg-text-dark-blue-500"
                />

                <Text
                  data-testid="extra-option-formatted-price"
                  className="gg-mr-1 gg-w-16 gg-shrink-0 gg-whitespace-nowrap gg-break-keep gg-text-secondary-text"
                >
                  {`+€${extraPrice},-`}
                </Text>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
export default ExtraOptionControls;
